import { ApolloLink, Observable } from '@apollo/client';
import { getFirebaseAuthToken } from 'redux/firebase';
import {
  captureCosneticsSession,
  rejectCosneticsSession,
  withCosneticsSession
} from 'services/session';
import { normalizeBaseURL } from 'services/session/SessionCoordinator';

interface GraphError {
  message?: string;
  extensions?: { code?: unknown };
}

const isCosneticsRejection = (error: GraphError): boolean => {
  const code = error.extensions?.code;
  return code === 'COSNETICS_AUTHENTICATION_REQUIRED' ||
    code === 'COSNETICS_UNAUTHENTICATED' ||
    // offkey-graph currently wraps Cosnetics failures in ordinary Errors; its
    // resolvers can add another "Error: " prefix. Do not treat Firebase auth
    // failures, generic UNAUTHENTICATED or authorization errors as Cosnetics401.
    /^(?:Error: )*API Error \(401\):/.test(error.message || '');
};

export const createSessionLink = () => new ApolloLink((operation, forward) =>
  new Observable((observer) => {
    let cancelled = false;

    const run = async () => {
      const session = captureCosneticsSession();
      // Firebase may renew asynchronously. Acquire the Cosnetics ticket after
      // that await so the request uses the latest shared token.
      const firebaseToken = await getFirebaseAuthToken();
      if (cancelled) return;

      await withCosneticsSession(async (ticket) => {
        if (cancelled) return;
        // Lender mutations put their API URL inside input; queries generally
        // pass it at the top level. Both must match the acquired session.
        for (const variables of [operation.variables, operation.variables.input]) {
          for (const name of ['baseURL', 'baseUrl']) {
            const requestedAPI = variables?.[name];
            if (typeof requestedAPI === 'string' && requestedAPI &&
                normalizeBaseURL(requestedAPI) !== normalizeBaseURL(ticket.baseURL)) {
              throw new Error('The operation API does not match the active Cosnetics session.');
            }
          }
        }
        const headers = { ...operation.getContext().headers };
        for (const name of Object.keys(headers)) {
          if (['cosneticstoken', 'firebasetoken'].includes(name.toLowerCase())) {
            delete headers[name];
          }
        }
        operation.setContext({
          headers: {
            ...headers,
            CosneticsToken: ticket.token,
            FirebaseToken: firebaseToken
          }
        });

        // The shared operation lock lasts until the HTTP operation finishes,
        // including all Cosnetics calls that offkey-graph performs internally.
        await new Promise<void>((resolve, reject) => {
          let authCheck = Promise.resolve();
          const inspect = (errors?: readonly GraphError[]) => {
            if (errors?.some(isCosneticsRejection)) {
              authCheck = authCheck.then(async () => {
                await rejectCosneticsSession(ticket);
              });
              // Keep a rejection handled while a streaming response is still
              // open; finish() below will propagate it to the operation.
              void authCheck.catch(() => {});
            }
          };
          const finish = async (error?: unknown) => {
            try {
              await authCheck;
              if (!cancelled) {
                if (error) observer.error(error);
                else observer.complete();
              }
              resolve();
            } catch (failure) {
              reject(failure);
            }
          };

          forward(operation).subscribe({
            next: (result) => {
              inspect(result.errors);
              if (!cancelled) observer.next(result);
            },
            error: (error) => {
              inspect(error?.graphQLErrors || error?.result?.errors);
              void finish(error);
            },
            complete: () => { void finish(); }
          });
        });
      }, session);
    };

    void run().catch((error) => {
      if (!cancelled) observer.error(error);
    });

    return () => {
      cancelled = true;
      // Before dispatch this cancels the operation. Once dispatched, keep the
      // inner subscription alive until the server responds: aborting fetch
      // cannot establish that a backend mutation stopped using its token.
      // Suppress delivery to the detached UI, while retaining the operation
      // lock. Transport failures/browser closure still require backend support
      // to guarantee an in-flight server operation has ended.
    };
  })
);
